import { RiCheckboxCircleFill, RiLoader4Line } from '@remixicon/react';

/**
 * Shared "we're processing your email link" splash used by any
 * post-verify landing (currently `/email-confirmation` and
 * `/register/team`). Keeps the visual language identical across
 * flows so future auth entry points (magic link, password reset,
 * etc.) get the same look for free.
 *
 * Props:
 *   title    — big heading. Pages pass warm context-specific copy
 *              (e.g. "Welcome to Test 3 ORg, Tools") or a generic
 *              label ("Email Confirmation").
 *   subtitle — optional secondary line under the title.
 *   message  — the currently-updating status line beneath the icon
 *              (e.g. "Verifying your invitation…" → "Setting up your
 *              account, Tools…" → "All set — taking you to your
 *              dashboard…").
 *   variant  — 'loading' (spinner) or 'success' (green check).
 */
export default function VerifySplash({
  title,
  subtitle,
  message,
  variant = 'loading',
}) {
  const isSuccess = variant === 'success';
  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="max-w-md w-full flex flex-col flex-1">
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">{title}</h2>
          {subtitle && <p className="text-gray-600 mt-2">{subtitle}</p>}
        </div>

        <div className="flex flex-col items-center justify-center flex-1 py-8">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
              isSuccess ? 'bg-green-50' : 'bg-primary-500/10'
            }`}
          >
            {isSuccess ? (
              <RiCheckboxCircleFill className="w-8 h-8 text-green-500" />
            ) : (
              <RiLoader4Line className="w-8 h-8 text-primary-500 animate-spin" />
            )}
          </div>
          {message && (
            <p className="text-sm text-gray-700 text-center min-h-[1.25rem]">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
